import { useSyncExternalStore } from "react";

export type Appearance = "light" | "dark" | "system";
export const APPEARANCE_KEY = "berrier.appearance";
export const APPEARANCE_EVENT = "berrier-appearance-change";
export function validAppearance(value: unknown): Appearance {
  return value === "light" || value === "dark" ? value : "system";
}
export function resolveAppearance(preference: Appearance, systemDark: boolean): "light" | "dark" {
  return preference === "system" ? (systemDark ? "dark" : "light") : preference;
}
export function getAppearance(): Appearance {
  try { return validAppearance(localStorage.getItem(APPEARANCE_KEY)); } catch { return "system"; }
}
export function applyAppearance(): void {
  const preference = getAppearance();
  document.documentElement.dataset.appearance = preference;
  document.documentElement.dataset.theme = resolveAppearance(preference, window.matchMedia("(prefers-color-scheme: dark)").matches);
  window.dispatchEvent(new Event(APPEARANCE_EVENT));
}
export function setAppearance(preference: Appearance): void {
  try { localStorage.setItem(APPEARANCE_KEY, preference); } catch { /* Private browsers can disallow storage. */ }
  document.documentElement.dataset.appearance = preference;
  document.documentElement.dataset.theme = resolveAppearance(preference, window.matchMedia("(prefers-color-scheme: dark)").matches);
  window.dispatchEvent(new Event(APPEARANCE_EVENT));
}
export function initializeAppearance(): void {
  applyAppearance();
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (document.documentElement.dataset.appearance === "system") applyAppearance();
  });
  window.addEventListener("storage", (event) => { if (event.key === APPEARANCE_KEY) applyAppearance(); });
}
function subscribe(notify: () => void): () => void {
  window.addEventListener(APPEARANCE_EVENT, notify);
  return () => window.removeEventListener(APPEARANCE_EVENT, notify);
}
export function useAppearance(): Appearance {
  return useSyncExternalStore(subscribe, () => validAppearance(document.documentElement.dataset.appearance), () => "system");
}
